import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { updateOranAlarm } from '@api/common';

import { IAlarmOran } from '@interfaces/alarms/alarm';
import { IAlarmSettingsDTO } from '@interfaces/alarms/dto/alarmsDTO';

import { ESnackbarTypes } from '@enums/common';

import { ACTIVE_ALARMS, ALARM_HISTORY, ALARM_LIST, ALARM_LIST_ORAN, EDIT_ALARM } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

import { useMutateAlarmsCreateNoVesRules } from './useMutateAlarmsCreateNoVesRules';
import { useMutateAlarmsDeleteNoVesRules } from './useMutateAlarmsDeleteNoVesRules';
import { useMutateAlarmsUpdateNoVesRules } from './useMutateAlarmsUpdateNoVesRules';

export const useMutateUpdateOranAlarm = (onSuccess: () => void) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const { addAlarmNoVesRules, isLoadingAddAlarmNoVesRules } = useMutateAlarmsCreateNoVesRules();
	const { deleteAlarmNoVesRules, isLoadingDeleteAlarmNoVesRules } = useMutateAlarmsDeleteNoVesRules();
	const { updateAlarmNoVesRules, isLoadingUpdateAlarmNoVesRules } = useMutateAlarmsUpdateNoVesRules();

	const {
		mutate: editOranAlarm,
		isLoading: isLoadingEditOranAlarm,
	} = useMutation<IAlarmSettingsDTO>({
		mutationKey: [EDIT_ALARM],
		mutationFn: updateOranAlarm,
		onSuccess: (data: IAlarmOran) => {
			dispatch(setSnackbar({
				message: responseMessages.alarmOranUpdated(data.name),
				type: ESnackbarTypes.success,
			}));

			if (onSuccess) {
				onSuccess();
			}

			return [
				queryClient.invalidateQueries([ALARM_LIST]),
				queryClient.invalidateQueries([ACTIVE_ALARMS]),
				queryClient.invalidateQueries([ALARM_HISTORY]),
				queryClient.invalidateQueries([ALARM_LIST_ORAN]),
			];
		},
	});

	const updateOranAlarmSettings = async (dto: IAlarmSettingsDTO, alarm?: IAlarmOran) => {
		const { networkElementFields, ...rest } = dto;

		if (networkElementFields) {
			const customSettings = alarm?.customSettings?.noVesEventAlarmCreateSettings || [];

			const fieldsToAdd = networkElementFields.filter((field) => !field?.id);

			const fieldsToDelete = customSettings
				.filter((field) => {
					return field?.id && !networkElementFields.find((setting) => setting.id === field.id);
				}).map((field) => ({ id: field.id }));

			const fieldsToUpdate = networkElementFields.filter((field) => {
				return field?.id && customSettings.find((setting) => {
					return setting.id === field.id && setting.noEventMinutes !== field.noEventMinutes;
				});
			})?.map((field) => ({ id: field.id, noEventMinutes: field.noEventMinutes }));

			if (fieldsToDelete.length) await deleteAlarmNoVesRules(fieldsToDelete);
			if (fieldsToUpdate.length) await updateAlarmNoVesRules(fieldsToUpdate);
			if (fieldsToAdd.length) await addAlarmNoVesRules(fieldsToAdd);
		}

		editOranAlarm(rest);
	};

	return {
		editOranAlarm: updateOranAlarmSettings,
		isLoadingEditOranAlarm: isLoadingEditOranAlarm
			|| isLoadingAddAlarmNoVesRules
			|| isLoadingDeleteAlarmNoVesRules
			|| isLoadingUpdateAlarmNoVesRules,
	};
};
